import {
  WIX_LOCK_FILENAME,
  WIX_CONFIG_FILENAME,
} from '@wix/cli-core-definitions';
import { VELO_DEPENDENCIES_CONFIG_FILENAME } from '@wix/cli-site-old-definitions';
import { createLogger as createCliLogger } from '@wix/cli-logger';
import chalk from 'chalk';
import terminalLink from 'terminal-link';
import type { TFunction } from '@wix/cli-i18n';
import type { PipelineDescription } from '@wix/ambassador-document-management-editor-deployments-v1-deployment/types';

export function createLogger({ t }: { t: TFunction }) {
  const logger = createCliLogger();

  const logAborting = () => logger.log(chalk.dim(t('general.aborting')));

  const logSyncingLocalCode = () => {
    const loader = logger.logWithLoader({
      text: t('dev_command.syncing_local_code_start'),
    });

    return {
      success: () => {
        const message = t('dev_command.syncing_local_code_end');
        loader.succeed(message);
        return message;
      },
      fail: () => loader.stop(),
    };
  };

  const logUpdatedLocalCodeToRevision = ({
    revision,
  }: {
    revision: string;
  }) => {
    const message = t('dev_command.revision_created', {
      revision,
      configFileName: WIX_CONFIG_FILENAME,
    });
    logger.log(message);
    return message;
  };

  const logRecoverableFailureToRemoveTypesDir = (error: unknown) => {
    const cause = getErrorMessage(error);

    logger
      .logWithLoader(t('dev_command.failed_to_delete_types_dir', { cause }))
      .fail();
    logNewLine();
  };

  const logSyncingTypesToRevision = (revision: string) => {
    const loader = logger.logWithLoader({
      text: t('dev_command.syncing_types_to_revision_start', { revision }),
    });

    return {
      success: () => {
        loader.succeed(
          t('dev_command.syncing_types_to_revision_end', { revision })
        );
      },
      fail: () => {
        loader.fail(t('dev_command.syncing_types_to_revision_fail_line1'));
        logger.log(
          `  ${t('dev_command.syncing_types_to_revision_fail_line2')}`
        );
        logNewLine();
        logger.log(
          `  ${t('dev_command.syncing_types_to_revision_fail_line3')}`
        );
        logNewLine();
      },
    };
  };

  const logSyncingPagesToRevision = (revision: string) => {
    const loader = logger.logWithLoader({
      text: t('dev_command.syncing_pages_to_revision_start', { revision }),
    });

    return {
      success: () => {
        loader.succeed(
          t('dev_command.syncing_pages_to_revision_end', { revision })
        );
      },
      fail: () => loader.fail(),
    };
  };

  const logNewLine = () => logger.log('');

  const logDependencyInstalling = (dependencyName: string) => {
    const loader = logger.logWithLoader({
      text: chalk.yellow(
        t('dependencies.installing_dependency', {
          dependency: chalk.bold.yellow(dependencyName),
        })
      ),
    });
    return {
      success: () => {
        loader.succeed(
          chalk.green(
            t('dependencies.dependency_installed', {
              dependency: chalk.bold.green(dependencyName),
            })
          )
        );
      },
      fail: () => loader.stop(),
    };
  };

  const logAllDependenciesInstalling = () => {
    const loader = logger.logWithLoader({
      text: chalk.yellow(t('dependencies.installing_all_dependencies')),
    });
    return {
      success: () => {
        loader.succeed(
          chalk.green(t('dependencies.all_dependencies_installed'))
        );
      },
      fail: () => loader.stop(),
    };
  };

  const logDependencyUninstalling = (dependencyName: string) => {
    const loader = logger.logWithLoader({
      text: chalk.yellow(
        t('dependencies.uninstalling_dependency', {
          dependency: chalk.bold.yellow(dependencyName),
        })
      ),
    });

    return {
      success: () => {
        loader.succeed(
          chalk.green(
            t('dependencies.dependency_uninstalled', {
              dependency: chalk.bold.green(dependencyName),
            })
          )
        );
      },
      fail: () => loader.stop(),
    };
  };

  const logOpeningBrowser = (https: boolean) => {
    logger.log(t('dev_command.opening_browser'));

    if (https) {
      logger.log(t('dev_command.dev_server_url.approve_cert'));
    }
  };

  const logNoOpenedTab = (devEditorUrl: string) => {
    const message = t('dev_command.no_open_editor', {
      key: chalk.bold.italic('e'),
      link: devEditorUrl,
    });
    logger.log(message);
    return message;
  };

  const logPublishRevision = ({
    branchName,
    isLocalDeployment,
    currentRevision,
    latestRevision,
    preview,
  }: {
    branchName: string | null;
    isLocalDeployment: boolean;
    currentRevision: string;
    latestRevision: string | null;
    preview:
      | {
          deploymentId: string;
          deploymentUrl: string;
          deploymentShortUrl?: string | null;
          deployedRevision: string;
        }
      | undefined;
  }) =>
    logger.log(`
  ${
    !preview
      ? ''
      : `${t('publish_command.publish_details_preview')}
  ${chalk.underline(
    chalk.blue(preview.deploymentShortUrl ?? preview.deploymentUrl)
  )}`
  }

  ${chalk.bold(t('publish_command.publish_details_about_to_publish'))}

    ${
      isLocalDeployment
        ? t('publish_command.prompt_what_to_publish_choice_local')
        : branchName
          ? t('publish_command.prompt_what_to_publish_choice_remote', {
              branchName,
            })
          : t('publish_command.prompt_what_to_publish_choice_remote_fallback')
    }
    ${t('publish_command.publish_details_ui_version', {
      revision: currentRevision,
    })}
    ${
      latestRevision && Number(latestRevision) > Number(currentRevision)
        ? `\n  ${chalk.red(
            `${chalk.bold(
              t(
                'publish_command.publish_details_newer_editor_version_caution_title'
              )
            )}: ${t(
              'publish_command.publish_details_newer_editor_version_caution_details'
            )} ${chalk.underline(
              chalk.blue(
                terminalLink(
                  t(
                    'publish_command.publish_details_newer_editor_version_link_text'
                  ),
                  t(
                    'publish_command.publish_details_newer_editor_version_link_url'
                  )
                )
              )
            )}`
          )}\n`
        : ''
    }
    ${
      isLocalDeployment
        ? chalk.red(
            `${chalk.bold(
              t('publish_command.publish_details_local_caution_title')
            )}: ${t('publish_command.publish_details_local_caution_details')}\n`
          )
        : ''
    }`);

  const logPipelineErrorInformation = ({
    pipelinesDescription,
  }: {
    pipelinesDescription: PipelineDescription[];
  }) => {
    const errorBadge = t('publish_command.publish_pipeline_error_badge');
    const taskDone = chalk.green(t('publish_command.generic_task_done'));
    const taskFailed = chalk.red(t('publish_command.generic_task_failed'));
    const divider = '-'.repeat(30);
    const padding = '  ';

    const normalizeText = (text: string) => {
      return text
        .split('\n')
        .map((line, index) => {
          if (line.trim() && index) {
            return `${padding}${line}`;
          }

          return line;
        })
        .join('\n');
    };

    let output = '';

    output += `\n${chalk.red(errorBadge)} ${t(
      'publish_command.publish_pipeline_error_title'
    )}\n`;
    output += `\n${divider}\n`;
    output += `Status: ${taskFailed}`;

    const tasksDescriptions = pipelinesDescription[0]?.tasksDescriptions;

    if (tasksDescriptions) {
      for (const task of tasksDescriptions) {
        output += `\n\n* ${task.taskName}... ${
          task.status === 'ERROR' ? `${taskFailed}:` : taskDone
        }\n`;

        if (task.description) {
          const errorDescription = JSON.parse(task.description);

          for (const fileName in errorDescription) {
            output += `\n${padding}${errorBadge} ${fileName} | `;
            output += normalizeText(errorDescription[fileName].toString());
          }
        }
      }
    }

    output += `\n${divider}`;

    logger.log(output);
  };

  const logPublishing = () => {
    const loader = logger.logWithLoader({
      text: t('publish_command.publishing'),
    });

    return {
      success: ({ siteUrl }: { siteUrl: string }) => {
        loader.succeed(
          t('publish_command.published', {
            url: chalk.underline(chalk.blue(siteUrl)),
          })
        );
      },
      fail: () => loader.stop(),
    };
  };

  const logDeploying = () => {
    const loader = logger.logWithLoader({
      text: t('publish_command.deploying'),
    });

    return {
      success: () => {
        loader.succeed(
          `${t('publish_command.deploying')} ${chalk.green(
            t('publish_command.generic_task_done')
          )}`
        );
      },
      fail: () =>
        loader.fail(
          `${t('publish_command.deploying')} ${chalk.red(
            t('publish_command.generic_task_failed')
          )}`
        ),
    };
  };

  const logForceDeployWarning = () =>
    logger.log(
      chalk.red(
        `${chalk.bold(
          t('publish_command.publish_with_errors_caution_title')
        )}: ${t('publish_command.publish_with_errors_caution_details')}\n`
      )
    );

  const logPublishAborted = () =>
    logger.log(`\n${t('publish_command.publish_aborted')}`);

  const logCreatingPreview = () =>
    logger.log(`\n${t('preview_command.creating_preview')}\n`);

  const logPreviewCreated = (url: string) =>
    logger.log(t('preview_command.preview_created', { url: chalk.green(url) }));

  const logPreviewUnsupported = () =>
    logger.log(t('preview_command.preview_unsupported'));

  const logFailedToGetLatestRevision = (error: unknown) => {
    const cause = error instanceof Error ? error.stack : error;
    logger.log(t('publish_command.failed_getting_latest_revision', { cause }));
  };

  const logMigratingToAnyNpm = () => {
    logger.log(
      `${t('install_command.migrating_to_any_npm', {
        wixLockFileName: WIX_LOCK_FILENAME,
        configFileName: VELO_DEPENDENCIES_CONFIG_FILENAME,
      })}\n`
    );
    logger.log(
      chalk.yellow(t('install_command.starting_migrating_to_any_npm'))
    );
  };

  const logMigrationToAnyNpmCompletedSuccessfully = () =>
    logger.log(chalk.green(t('install_command.migration_to_any_npm_success')));

  const logMigrationToAnyNpmFailed = () => {
    logger.log(chalk.red(t('install_command.migration_to_any_npm_failed')));
  };

  const logPreviewing = () => {
    const loader = logger.logWithLoader({
      text: t('publish_command.creating_preview'),
    });

    return {
      success: () =>
        loader.succeed(t('publish_command.creating_preview_success')),
      fail: () => loader.fail(t('publish_command.creating_preview_failed')),
      stop: () => loader.stop(),
    };
  };

  return {
    t,
    logAborting,
    logSyncingLocalCode,
    logRecoverableFailureToRemoveTypesDir,
    logUpdatedLocalCodeToRevision,
    logSyncingPagesToRevision,
    logSyncingTypesToRevision,
    logDependencyInstalling,
    logDependencyUninstalling,
    logAllDependenciesInstalling,
    logOpeningBrowser,
    logNoOpenedTab,
    logPublishRevision,
    logPublishing,
    logPublishAborted,
    logCreatingPreview,
    logPreviewCreated,
    logPreviewUnsupported,
    logNewLine,
    logFailedToGetLatestRevision,
    logMigratingToAnyNpm,
    logMigrationToAnyNpmCompletedSuccessfully,
    logMigrationToAnyNpmFailed,
    logDeploying,
    logForceDeployWarning,
    logPipelineErrorInformation,
    logPreviewing,
  };
}

function getErrorMessage(cause: unknown) {
  return cause instanceof Error ? cause.message : JSON.stringify(cause);
}

export type Logger = ReturnType<typeof createLogger>;
